import { ReactNode, useContext, ChangeEvent, FormEvent } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Permit, Status, PhoneType } from 'types';
import { getCountryFromStateCode } from 'utils';
import { AppContext } from 'context/appContext';
import MakeModelAutocomplete from 'components/autocompletes/MakeModelAutocomplete';
import AuturaTextField from 'components/inputs/AuturaTextField';
import AuturaPhoneField from 'components/inputs/AuturaPhoneField';
import FormFrame from 'components/layout/FormFrame';
import { ScrollableFormContainer } from 'components/layout/ScrollableContainer';
import SettingsSection from 'components/layout/SettingsSection';
import AuturaStateField from 'components/inputs/AuturaStateField';
import AuturaDropdown from 'components/inputs/AuturaDropdown';
import AuturaDateTimePicker from '../../../components/inputs/AuturaDateTimePicker';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { CorrectionFormProps } from 'components/importer/PreviewStep';

const PermitCorrectionForm = ({
  entity: permit,
  setEntity: setPermit,
}: CorrectionFormProps<Permit>): ReactNode => {
  const { t } = useTranslation();
  const {
    state: { tenant, colors },
  } = useContext(AppContext);

  // field update handlers
  const handleSetStateCode = (stateCode: string) => {
    const country = getCountryFromStateCode(stateCode);
    if (permit) {
      setPermit({
        ...permit,
        contact: {
          ...permit.contact,
          id: permit.contact?.id || 0,
          status: permit.contact?.status || Status.Active,
          tenantID: tenant.id,
          name: permit.contact?.name || '',
          location: {
            ...permit.contact?.location,
            stateCode,
            country,
          },
        },
      });
    }
  };
  const handleSetVehiclePlateState = (stateCode: string) => {
    if (permit) {
      setPermit({
        ...permit,
        vehicle: {
          ...permit.vehicle,
          plate: { ...permit.vehicle.plate, state: stateCode },
        },
      });
    }
  };
  const handleSetPhoneNumber = (phone: string) => {
    if (!permit) {
      return;
    }
    const updatedPermit: Permit = {
      ...permit,
    };
    updatedPermit.contact.primaryPhone = phone.replace(/\D/g, '');
    setPermit(updatedPermit);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!permit) {
      return;
    }
    const updatedPermit: Permit = {
      ...permit,
    };
    const { name, value } = event.target;
    switch (name) {
      case 'permit.vehicle.make':
        updatedPermit.vehicle.make = value;
        break;
      case 'permit.vehicle.model':
        updatedPermit.vehicle.model = value;
        break;
      case 'permit.vehicle.year':
        updatedPermit.vehicle.year = parseInt(value) || 0;
        break;
      case 'permit.vehicle.color':
        updatedPermit.vehicle.color = value;
        break;
      case 'permit.vehicle.plate.number':
        updatedPermit.vehicle.plate.number = value.toUpperCase();
        break;
      case 'permit.vehicle.plate.state':
        updatedPermit.vehicle.plate.state = value.toUpperCase();
        break;
      case 'permit.vehicle.vin':
        updatedPermit.vehicle.vin = value.toUpperCase();
        break;
      case 'permit.vehicle.trim':
        updatedPermit.vehicle.trim = value;
        break;
      case 'permit.contact.name':
        updatedPermit.contact.name = value.trim();
        break;
      case 'permit.contact.primaryEmail':
        updatedPermit.contact.primaryEmail = value.trim();
        break;
      case 'permit.contact.primaryPhone':
        updatedPermit.contact.primaryPhone = value.replace(/\D/g, '');
        break;
      case 'permit.contact.location.street':
        updatedPermit.contact.location.street = value;
        break;
      case 'permit.contact.location.unit':
        updatedPermit.contact.location.unit = value;
        break;
      case 'permit.contact.location.city':
        updatedPermit.contact.location.city = value;
        break;
      case 'permit.contact.location.postalCode':
        updatedPermit.contact.location.postalCode = value;
        break;
      case 'permit.permitNumber':
        updatedPermit.permitNumber = value;
        break;
    }
    setPermit(updatedPermit);
  };

  const preventSubmit = (event: FormEvent<Element>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <ValidatorForm onSubmit={preventSubmit}>
      <FormFrame loading={false}>
        <ScrollableFormContainer maxHeight={`calc(100dvh - 372px)`}>
          <SettingsSection
            title={t('correction_title', { ns: 'importWizard' })}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaTextField
                  name={'permit.contact.name'}
                  label={t('permit.contact.name', { ns: 'entities' })}
                  value={permit?.contact?.name || ''}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaDateTimePicker
                  label={t('permit.startTime', { ns: 'entities' })}
                  required
                  dateTime={
                    permit?.startTime ? new Date(permit.startTime) : new Date()
                  }
                  setDateTime={(date: Date | null) => {
                    if (permit) {
                      setPermit({
                        ...permit,
                        startTime: date ? date.getTime() : 0,
                      });
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaDateTimePicker
                  label={t('permit.endTime', { ns: 'entities' })}
                  dateTime={permit?.endTime ? new Date(permit.endTime) : null}
                  setDateTime={(date: Date | null) => {
                    if (permit) {
                      setPermit({
                        ...permit,
                        endTime: date ? date.getTime() : 0,
                      });
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit.contact.location.street', {
                    ns: 'entities',
                  })}
                  name='permit.contact.location.street'
                  value={permit?.contact?.location?.street || ''}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit.contact.location.unit', {
                    ns: 'entities',
                  })}
                  name='permit.contact.location.unit'
                  value={permit?.contact?.location?.unit || ''}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit.contact.location.city', {
                    ns: 'entities',
                  })}
                  name='permit.contact.location.city'
                  value={permit?.contact?.location?.city || ''}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaStateField
                  required
                  stateCode={permit?.contact?.location?.stateCode || ''}
                  setStateCode={handleSetStateCode}
                  label={t('permit.contact.location.stateCode', {
                    ns: 'entities',
                  })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit.contact.location.postalCode', {
                    ns: 'entities',
                  })}
                  name='permit.contact.location.postalCode'
                  value={permit?.contact?.location?.postalCode || ''}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit.contact.primaryEmail', { ns: 'entities' })}
                  name={'permit.contact.primaryEmail'}
                  value={permit?.contact?.primaryEmail || ''}
                  validators={['isEmail']}
                  errorMessages={[t('invalidEmail')]}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaPhoneField
                  label={t(`permit.contact.primaryPhone`, { ns: 'entities' })}
                  name={'permit.contact.primaryPhone'}
                  typeFieldName={'permit.contact.primaryPhoneType'}
                  typeFieldLabel='Type'
                  phoneNumber={permit?.contact?.primaryPhone || ''}
                  setPhoneNumber={handleSetPhoneNumber}
                  phoneType={permit?.contact?.primaryPhoneType}
                  setPhoneType={(phoneType) => {
                    if (permit?.contact) {
                      const updatedPermit = {
                        ...permit,
                        contact: {
                          ...permit.contact,
                          primaryPhoneType: phoneType,
                        },
                      };
                      setPermit(updatedPermit);
                    }
                  }}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('permit.vehicle.label', { ns: 'entities' })}
            divider={false}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit.vehicle.plate.number', { ns: 'entities' })}
                  name='permit.vehicle.plate.number'
                  value={permit?.vehicle?.plate?.number || ''}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaStateField
                  label={t('permit.vehicle.plate.state', { ns: 'entities' })}
                  stateCode={permit?.vehicle?.plate?.state || ''}
                  setStateCode={handleSetVehiclePlateState}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit.vehicle.year', { ns: 'entities' })}
                  name={'permit.vehicle.year'}
                  value={permit?.vehicle?.year || ''}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaDropdown
                  label={t('permit.vehicle.color', { ns: 'entities' })}
                  name='newValue'
                  value={permit?.vehicle?.color.toLowerCase() || ''}
                  setValue={(e: ChangeEvent<HTMLInputElement>) => {
                    if (permit) {
                      setPermit({
                        ...permit,
                        vehicle: {
                          ...permit.vehicle,
                          color: e.target.value,
                        },
                      });
                    }
                  }}
                  options={colors.map((color: string) => color)}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <MakeModelAutocomplete
                  make={permit?.vehicle?.make || ''}
                  model={permit?.vehicle?.model || ''}
                  setMake={(make: string | null) => {
                    if (permit) {
                      setPermit({
                        ...permit,
                        vehicle: {
                          ...permit.vehicle,
                          make: make || '',
                        },
                      });
                    }
                  }}
                  setModel={(model: string | null) => {
                    if (permit) {
                      setPermit({
                        ...permit,
                        ...permit.vehicle,
                        vehicle: {
                          ...permit.vehicle,
                          model: model || '',
                        },
                      });
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit.vehicle.vin', { ns: 'entities' })}
                  name='permit.vehicle.vin'
                  value={permit?.vehicle?.vin || ''}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('permit_type.label', { ns: 'entities' })}
                  name='permit.permitType'
                  value={permit?.permitType.name}
                  required
                  readonly
                />
              </Grid>
            </Grid>
          </SettingsSection>
        </ScrollableFormContainer>
      </FormFrame>
    </ValidatorForm>
  );
};

export default PermitCorrectionForm;
